import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import { ERP_BRAND, MOVEMENT_STATUS_LABELS, movementStatusColor } from "../../../utils/erp";

export function ErpPanel({ children, sx = {}, ...props }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: 3, sm: 4 },
        border: `1px solid ${ERP_BRAND.border}`,
        overflow: "hidden",
        minWidth: 0,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Card>
  );
}

export function ErpMetricCard({ icon, label, value, detail, tone = "orange" }) {
  const tones = {
    orange: { bg: "rgba(243,90,58,0.1)", color: ERP_BRAND.orange },
    green: { bg: "rgba(39,174,96,0.11)", color: "#1D8C4A" },
    blue: { bg: "rgba(47,128,237,0.11)", color: "#2F80ED" },
    dark: { bg: "rgba(25,36,47,0.08)", color: ERP_BRAND.dark },
    red: { bg: "rgba(211,47,47,0.1)", color: "#C62828" },
  };
  const selected = tones[tone] || tones.orange;

  return (
    <ErpPanel sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850, letterSpacing: 0.3 }}>
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: ERP_BRAND.dark,
                fontWeight: 950,
                mt: 0.65,
                fontSize: { xs: 20, sm: 24 },
                lineHeight: 1.15,
                overflowWrap: "anywhere",
              }}
            >
              {value}
            </Typography>
            {detail ? (
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.6 }}>
                {detail}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: selected.bg,
              color: selected.color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </ErpPanel>
  );
}

export function ErpSectionTitle({ title, description, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={1.5}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 950,
            color: ERP_BRAND.dark,
            fontSize: { xs: 20, sm: 24 },
            lineHeight: 1.15,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.45 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {action ? (
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            maxWidth: "100%",
            flexShrink: 0,
            "& > .MuiStack-root": {
              width: { xs: "100%", sm: "auto" },
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: { sm: "wrap" },
            },
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          {action}
        </Box>
      ) : null}
    </Stack>
  );
}

export function ErpLoading({ label = "Cargando información..." }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 220 }}>
      <CircularProgress size={34} />
      <Typography sx={{ color: ERP_BRAND.muted, fontWeight: 750 }}>{label}</Typography>
    </Stack>
  );
}

export function ErpEmpty({ title = "Sin registros", description = "Todavía no hay información para mostrar.", action }) {
  return (
    <Stack
      alignItems="center"
      textAlign="center"
      spacing={1.2}
      sx={{ py: { xs: 4, sm: 6 }, px: { xs: 1.5, sm: 2 }, minWidth: 0 }}
    >
      <Box
        sx={{
          width: 58,
          height: 58,
          borderRadius: 4,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(25,36,47,0.06)",
          color: "rgba(25,36,47,0.48)",
        }}
      >
        <InboxRoundedIcon />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>
        {title}
      </Typography>
      <Typography sx={{ color: ERP_BRAND.muted, maxWidth: 520 }}>{description}</Typography>
      {action ? (
        <Box sx={{ pt: 0.5, width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
          {action}
        </Box>
      ) : null}
    </Stack>
  );
}

export function MovementStatusChip({ status, size = "small" }) {
  return (
    <Chip
      size={size}
      color={movementStatusColor(status)}
      label={MOVEMENT_STATUS_LABELS[status] || status || "—"}
      sx={{ fontWeight: 850 }}
    />
  );
}
